import { Reducer } from 'react';
import _ from 'lodash';
import ContentConstants from '../contentConstants';
import { EditState, IHasValidationModel, IReportingUser } from '../models';
import * as ClientAPI from '../services/ClientAPI';
import * as ReportingUsersAPI from '../services/ReportingUsersAPI';
import * as SurveyAPI from '../services/SurveyAPI';
import { ReportingHelper, ReportingSessionStorageHandler } from '../utilities';
import { IApplicationState } from './';
import {
	IReportingAccessAddUser,
	IReportingAccessDeleteUser,
	IReportingAccessGetUsersRequested,
	IReportingAccessGetUsersSucceeded,
	IReportingAccessNotifyUserFailed,
	IReportingAccessNotifyUserRequested,
	IReportingAccessNotifyUserSucceeded,
	IReportingAccessSaveUsersFailed,
	IReportingAccessSaveUsersRequested,
	IReportingAccessSaveUsersSucceeded,
	IReportingAccessUpdateUser,
	IReportingClientMinNGetFailed,
	IReportingClientMinNGetRequested,
	IReportingClientMinNGetSucceeded,
	IReportingSetupMinNUpdated,
	IReportingSurveyMinNGetFailed,
	IReportingSurveyMinNGetRequested,
	IReportingSurveyMinNGetSucceeded,
	IReportingSurveyMinNSaveFailed,
	IReportingSurveyMinNSaveRequested,
	IReportingSurveyMinNSaveSucceeded,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IReportingAccessState extends IHasValidationModel {
	clientMinN?: number;
	isSavingSurveyMinN: boolean;
	isSavingUsers: boolean;
	isLoadingUsers: boolean;
	isSendingEmail: boolean;
	minNValidationModel?: IHasValidationModel;
	reportingUsers: IReportingUser[];
	reportingUsersValidationModel?: IHasValidationModel;
	surveyMinN?: number;
}

export const defaultState: IReportingAccessState = {
	clientMinN: undefined,
	isSavingSurveyMinN: false,
	isSavingUsers: false,
	isLoadingUsers: false,
	isSendingEmail: false,
	isValid: true,
	reportingUsers: [],
	surveyMinN: undefined,
};

export const ActionCreators = {
	AddUser: (surveyId: number) => (dispatch: any, getState: () => IApplicationState) => {
		const reportingUsers = getState().reportingAccess.reportingUsers;
		const newUser = ReportingHelper.initializeNewUser(surveyId, reportingUsers.length);
		dispatch({ type: ActionTypes.REPORTING_ACCESS_ADD_USER, payload: newUser });
	},
	DeleteUser: (user: IReportingUser) => ({ type: ActionTypes.REPORTING_ACCESS_DELETE_USER, payload: user }),
	GetUsers: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_ACCESS_GET_USERS_REQUESTED });
		return ReportingUsersAPI.getUsers(surveyId).then((users: IReportingUser[]) => {
			const retrievedUsers = ReportingSessionStorageHandler.appendSessionEmailInfo(surveyId, users);
			dispatch({ type: ActionTypes.REPORTING_ACCESS_GET_USERS_SUCCEEDED, payload: retrievedUsers });
		});
	},
	GetClientMinN: () => async (dispatch: any, getState: () => IApplicationState) => {
		try {
			dispatch({ type: ActionTypes.REPORTING_CLIENT_MIN_N_GET_REQUESTED });
			const externalClientId = getState().oidc.user?.profile.external_clientid;
			const clientMinN = await ClientAPI.getMinN(externalClientId);
			dispatch({ type: ActionTypes.REPORTING_CLIENT_MIN_N_GET_SUCCEEDED, payload: clientMinN });
		} catch (error) {
			dispatch({ type: ActionTypes.REPORTING_CLIENT_MIN_N_GET_FAILED });
		}
	},
	GetSurveyMinN: (surveyId: number) => async (dispatch: any, getState: () => IApplicationState) => {
		try {
			dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_GET_REQUESTED });
			const surveyMinN = await SurveyAPI.getSurveyMinN(surveyId);
			dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_GET_SUCCEEDED, payload: surveyMinN });
		} catch (error) {
			dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_GET_FAILED });
		}
	},
	MinNChange: (e: any) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_MIN_N_UPDATED, payload: e.target.value });
	},
	NotifyUser: (surveyId: number, user: IReportingUser) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });
		dispatch({ type: ActionTypes.REPORTING_ACCESS_NOTIFY_USER_REQUESTED });
		return ReportingUsersAPI.notifyUser(surveyId, user)
			.then(() => {
				dispatch({
					type: ActionTypes.TOAST_SUCCESS_NOTIFICATION,
					payload: ContentConstants.TOAST_REPORTING_USER_EMAIL_SENT,
				});
				const updatedUsers = getState().reportingAccess.reportingUsers.map((currentUser) =>
					currentUser.id === user.id ? { ...currentUser, hasSentEmail: true } : { ...currentUser },
				);
				ReportingSessionStorageHandler.saveToSessionStorage(surveyId, updatedUsers);
				dispatch({ type: ActionTypes.REPORTING_ACCESS_NOTIFY_USER_SUCCEEDED, payload: updatedUsers });
			})
			.catch((result: Error) => {
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: result.message });
				dispatch({ type: ActionTypes.REPORTING_ACCESS_NOTIFY_USER_FAILED });
			});
	},
	Save: (surveyId: number, reportingUsers: IReportingUser[]) => async (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });
		try {
			// Save reporting users
			const reportingUsersResult = await ReportingUsersAPI.saveUsers(surveyId, reportingUsers);
			if (reportingUsersResult.isValid) {
				const emailSentUsers: IReportingUser[] = reportingUsers.map((user) =>
					user.editState === EditState.Add ? { ...user, hasSentEmail: true } : { ...user },
				);
				ReportingSessionStorageHandler.saveToSessionStorage(
					surveyId,
					emailSentUsers.filter((user) => user.editState !== EditState.Remove),
				);
				dispatch({ type: ActionTypes.REPORTING_ACCESS_SAVE_USERS_SUCCEEDED, payload: reportingUsersResult.subject });
			} else {
				dispatch({ type: ActionTypes.REPORTING_ACCESS_SAVE_USERS_FAILED, payload: reportingUsersResult });
				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: ContentConstants.TOAST_REPORTING_USER_SAVE_FAILED,
						messageTitle: ContentConstants.TOAST_REPORTING_USER_FAILED_TITLE,
					},
				});
			}

			if (reportingUsersResult.isValid) {
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			}
		} catch (error) {
			dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
		}
	},
	SaveSurveyMinN: (surveyId: number) => async (dispatch: any, getState: () => IApplicationState) => {
		// Save MinN
		const state: IApplicationState = getState();
		const { surveyMinN } = state.reportingAccess;

		dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_REQUESTED });
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });

		const minNResult = await SurveyAPI.saveSurveyMinN(surveyId, surveyMinN);

		if (minNResult.isValid) {
			dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_SUCCEEDED, payload: minNResult });
		} else {
			dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			dispatch({ type: ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_FAILED, payload: minNResult });
		}
	},
	UpdateUser: (user: IReportingUser) => (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_ACCESS_UPDATE_USER, payload: user });
	},
};

export type KnownActions =
	| IReportingAccessSaveUsersRequested
	| IReportingAccessSaveUsersSucceeded
	| IReportingAccessSaveUsersFailed
	| IReportingAccessGetUsersRequested
	| IReportingAccessGetUsersSucceeded
	| IReportingAccessAddUser
	| IReportingAccessUpdateUser
	| IReportingAccessDeleteUser
	| IReportingAccessNotifyUserRequested
	| IReportingAccessNotifyUserSucceeded
	| IReportingAccessNotifyUserFailed
	| IReportingClientMinNGetSucceeded
	| IReportingClientMinNGetRequested
	| IReportingClientMinNGetFailed
	| IReportingSurveyMinNGetRequested
	| IReportingSurveyMinNGetSucceeded
	| IReportingSurveyMinNGetFailed
	| IReportingSurveyMinNSaveRequested
	| IReportingSurveyMinNSaveSucceeded
	| IReportingSurveyMinNSaveFailed
	| IReportingSetupMinNUpdated;

export const reducer: Reducer<IReportingAccessState, KnownActions> = (
	state: IReportingAccessState = defaultState,
	action: KnownActions,
): IReportingAccessState => {
	switch (action.type) {
		case ActionTypes.REPORTING_ACCESS_GET_USERS_REQUESTED:
		case ActionTypes.REPORTING_SURVEY_MIN_N_GET_REQUESTED: {
			return { ...state, isLoadingUsers: true };
		}
		case ActionTypes.REPORTING_ACCESS_GET_USERS_SUCCEEDED: {
			const reportingUsers = action.payload.map((user: IReportingUser, id: number) => ({ ...user, id }));
			return {
				...state,
				isLoadingUsers: false,
				reportingUsers,
			};
		}
		case ActionTypes.REPORTING_ACCESS_SAVE_USERS_REQUESTED: {
			return { ...state, isSavingUsers: true };
		}
		case ActionTypes.REPORTING_ACCESS_SAVE_USERS_FAILED: {
			return { ...state, isSavingUsers: false, reportingUsersValidationModel: action.payload };
		}
		case ActionTypes.REPORTING_ACCESS_SAVE_USERS_SUCCEEDED: {
			const resetUsers: IReportingUser[] = action.payload
				.filter((user: IReportingUser) => user.editState !== EditState.Remove)
				.map((user: IReportingUser) => ({ ...user, editState: EditState.None }));
			return { ...state, isSavingUsers: false, reportingUsers: resetUsers };
		}
		case ActionTypes.REPORTING_ACCESS_ADD_USER: {
			const users: IReportingUser[] = [...state.reportingUsers, action.payload];
			return { ...state, reportingUsers: users };
		}
		case ActionTypes.REPORTING_ACCESS_UPDATE_USER: {
			const editState = action.payload.editState === EditState.Add ? action.payload.editState : EditState.Update;
			const reportingUsers: IReportingUser[] = [...state.reportingUsers];
			const index = _.findIndex(reportingUsers, (user: IReportingUser) => user.id === action.payload.id);
			reportingUsers[index] = { ...action.payload, editState };
			return { ...state, reportingUsers };
		}
		case ActionTypes.REPORTING_ACCESS_DELETE_USER: {
			const reportingUsers: IReportingUser[] = [...state.reportingUsers];
			const index = _.findIndex(reportingUsers, (user: IReportingUser) => user.id === action.payload.id);
			reportingUsers[index] = { ...action.payload, editState: EditState.Remove };

			return { ...state, reportingUsers };
		}
		case ActionTypes.REPORTING_ACCESS_NOTIFY_USER_REQUESTED: {
			return { ...state, isSendingEmail: true };
		}
		case ActionTypes.REPORTING_ACCESS_NOTIFY_USER_SUCCEEDED: {
			return { ...state, isSendingEmail: false, reportingUsers: action.payload };
		}
		case ActionTypes.REPORTING_ACCESS_NOTIFY_USER_FAILED: {
			return { ...state, isSendingEmail: false };
		}
		case ActionTypes.REPORTING_CLIENT_MIN_N_GET_SUCCEEDED: {
			return { ...state, isLoadingUsers: false, clientMinN: action.payload };
		}
		case ActionTypes.REPORTING_SURVEY_MIN_N_GET_SUCCEEDED: {
			return { ...state, isLoadingUsers: false, surveyMinN: action.payload };
		}
		case ActionTypes.REPORTING_SURVEY_MIN_N_GET_FAILED: {
			return { ...state, isLoadingUsers: false };
		}
		case ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_REQUESTED: {
			return { ...state, isSavingSurveyMinN: true };
		}
		case ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_SUCCEEDED: {
			return {
				...state,
				isSavingSurveyMinN: false,
				minNValidationModel: action.payload,
				surveyMinN: action.payload.subject,
			};
		}
		case ActionTypes.REPORTING_SURVEY_MIN_N_SAVE_FAILED: {
			return { ...state, isSavingSurveyMinN: false, minNValidationModel: action.payload };
		}
		case ActionTypes.REPORTING_MIN_N_UPDATED: {
			return { ...state, surveyMinN: action.payload };
		}
	}
	return state;
};
